<script setup>
/**
 * GoalsSummarySection
 * Displays a summary of current goals using existing GoalCurrentList
 * Includes drill-down functionality to view agent details
 */
import { ref, computed, onMounted, onUnmounted } from 'vue'
import { storeToRefs } from 'pinia'
import { useGoals } from '@/core/composables/useGoals'
import { useAuthStore } from '@/app/store/auth'
import GoalCard from '@/ui/components/goals/GoalCard.vue'
import AgentGoalDetails from '@/ui/components/goals/AgentGoalDetails.vue'

const props = defineProps({
  highlightAgentId: {
    type: [String, Number],
    default: null,
  },
  showTutorial: {
    type: Boolean,
    default: false,
  },
})

const authStore = useAuthStore()
const { currentGoals, subscribeToCurrentGoals, unsubscribeAll, loading } = useGoals()

// Check if admin
const { isAdmin } = storeToRefs(authStore)

// Selected agent/goal for drill-down modal
const selectedGoalData = ref(null)

// Filter active goals for display
const activeGoals = computed(() => {
  return currentGoals.value.filter((goal) => goal.active || isAdmin.value)
})

// Handle agent row click - show drill-down modal
function handleAgentClick(data) {
  // Only allow if admin or user is the agent (claim-based)
  if (isAdmin.value || authStore.userAgentId === data.agent?.id) {
    selectedGoalData.value = data
  }
}

// Close drill-down modal
function closeDetails() {
  selectedGoalData.value = null
}

// Subscribe to goals on mount
onMounted(() => {
  subscribeToCurrentGoals()
})

// Cleanup on unmount
onUnmounted(() => {
  unsubscribeAll()
})
</script>

<template>
  <div class="goals-summary-section">
    <div class="section-header">
      <h3 class="section-title">יעדים נוכחיים</h3>
    </div>

    <div v-if="loading" class="loading-state">
      <div class="spinner"></div>
      <span>טוען יעדים...</span>
    </div>

    <div v-else-if="activeGoals.length === 0" class="empty-state">
      <p>אין יעדים פעילים כרגע</p>
    </div>

    <div v-else class="goals-grid">
      <GoalCard
        v-for="(goal, index) in activeGoals"
        :key="goal.uid"
        :goal="goal"
        :show-actions="false"
        :highlight-agent-id="props.highlightAgentId"
        :show-tutorial="props.showTutorial && index === 0"
        @agent-click="handleAgentClick"
      />
    </div>

    <!-- Agent Goal Details Modal -->
    <AgentGoalDetails v-if="selectedGoalData" :data="selectedGoalData" @close="closeDetails" />
  </div>
</template>

<style scoped>
.goals-summary-section {
  background: var(--bg-primary);
  border: 1px solid var(--border-color);
  border-radius: var(--border-radius-lg);
  overflow: hidden;
}

.section-header {
  padding: var(--spacing-md);
  background: var(--bg-secondary);
  border-bottom: 1px solid var(--border-color);
}

.section-title {
  margin: 0;
  font-size: var(--font-size-base);
  font-weight: var(--font-weight-semibold);
  color: var(--text-primary);
}

.loading-state,
.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: var(--spacing-sm);
  padding: var(--spacing-xl);
  color: var(--text-muted);
}

.spinner {
  width: 24px;
  height: 24px;
  border: 2px solid var(--border-color);
  border-top-color: var(--color-primary);
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

.goals-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(400px, 1fr));
  gap: var(--spacing-md);
  padding: var(--spacing-md);
  max-height: 500px;
  overflow-y: auto;
}

/* Responsive */
@media (max-width: 768px) {
  .goals-grid {
    grid-template-columns: 1fr;
  }
}
</style>
